<script lang="ts">
	import Icon from '$lib/components/Icon/Icon.svelte';
	import type { Icon as IconType } from '$lib/components/Icon/Icon-types';
	import Popover from '$lib/components/Popover/Popover.svelte';

	import type { Editor } from 'svelte-tiptap';

	export let editor: Editor;
	let element: HTMLElement;

	const FORMATTING = [
		'title',
		'heading',
		'subheading',
		'quote',
		'bullet-list',
		'numbered-list',
		'code',
	] as const;
	type FormattingIcon = (typeof FORMATTING)[number];

	const LAYOUT = [
		'paragraph-align-left',
		'paragraph-align-right',
		'paragraph-align-center',
		'paragraph-align-justify',
	] as const;

	type LayoutIcon = (typeof LAYOUT)[number];

	type AllIcons = FormattingIcon | LayoutIcon | 'text-scale';

	const isFormattingIcon = (icon: IconType): icon is FormattingIcon =>
		FORMATTING.includes(icon as FormattingIcon);
	const isLayoutIcon = (icon: IconType): icon is LayoutIcon => LAYOUT.includes(icon as LayoutIcon);

	const ALL: Array<AllIcons> = [...FORMATTING, ...LAYOUT, 'text-scale'];

	const toggleFormatting = (icon: FormattingIcon) => {
		switch (icon) {
			case 'title':
				editor?.chain().focus().setParagraph().run();
				editor?.chain().focus().unsetBlockquote().run();
				if (editor?.isActive('bulletList')) {
					editor?.chain().focus().toggleBulletList().run();
				}
				if (editor?.isActive('orderedList')) {
					editor?.chain().focus().toggleOrderedList().run();
				}
				break;
			case 'heading':
				editor?.chain().focus().toggleHeading({ level: 1 }).run();
				break;
			case 'subheading':
				editor?.chain().focus().toggleHeading({ level: 2 }).run();
				break;
			case 'quote':
				editor?.chain().focus().toggleBlockquote().run();
				break;
			case 'bullet-list':
				editor?.chain().focus().toggleBulletList().run();
				break;
			case 'numbered-list':
				editor?.chain().focus().toggleOrderedList().run();
				break;
			case 'code':
				editor?.chain().focus().toggleCodeBlock().run();
				break;
		}
	};

	const toggleLayout = (icon: LayoutIcon) => {
		switch (icon) {
			case 'paragraph-align-left':
				editor?.commands.setTextAlign('left');
				break;
			case 'paragraph-align-right':
				editor?.commands.setTextAlign('right');
				break;
			case 'paragraph-align-center':
				editor?.commands.setTextAlign('center');
				break;
			case 'paragraph-align-justify':
				editor?.commands.setTextAlign('justify');
				break;
		}
	};

	$: getActiveIcon = (): FormattingIcon => {
		if (editor?.isActive('heading', { level: 1 })) {
			return 'heading';
		} else if (editor?.isActive('heading', { level: 2 })) {
			return 'subheading';
		} else if (editor?.isActive('blockquote')) {
			return 'quote';
		} else if (editor?.isActive('bulletList')) {
			return 'bullet-list';
		} else if (editor?.isActive('orderedList')) {
			return 'numbered-list';
		} else if (editor?.isActive('paragraph')) {
			return 'title';
		} else if (editor?.isActive('codeBlock')) {
			return 'code';
		} else {
			return 'title';
		}
	};

	$: isSelected = (icon: AllIcons) => {
		switch (icon) {
			case 'title':
				return editor?.isActive('paragraph');
			case 'heading':
				return editor?.isActive('heading', { level: 1 });
			case 'subheading':
				return editor?.isActive('heading', { level: 2 });
			case 'quote':
				return editor?.isActive('blockquote');
			case 'bullet-list':
				return editor?.isActive('bulletList');
			case 'numbered-list':
				return editor?.isActive('orderedList');
			case 'code':
				return editor?.isActive('codeBlock');
			case 'paragraph-align-left':
				return editor?.isActive({ textAlign: 'left' });
			case 'paragraph-align-right':
				return editor?.isActive({ textAlign: 'right' });
			case 'paragraph-align-center':
				return editor?.isActive({ textAlign: 'center' });
			case 'paragraph-align-justify':
				return editor?.isActive({ textAlign: 'justify' });
			default:
				return false;
		}
	};
</script>

<div>
	<div bind:this={element}>
		<span>
			<Popover position="inline-start" color="#2a3039">
				<button type="button" slot="target">
					<Icon color="#98a5b3" size="24" icon={getActiveIcon()} />
					<Icon color="#98a5b3" size="24" icon="dropdown-arrow" />
				</button>
				<ul slot="content" class="formattingButton">
					{#each ALL as icon}
						<li>
							<div class="menuItem">
								<Icon
									onClick={() => {
										if (isFormattingIcon(icon)) {
											toggleFormatting(icon);
										} else if (isLayoutIcon(icon)) {
											toggleLayout(icon);
										} else if (icon == 'text-scale') {
											editor?.commands.setNextFontSize();
										}
									}}
									{icon}
									size="24"
									color={isSelected(icon) ? '#3f8ce8' : 'white'}
								/>
							</div>
						</li>
					{/each}
				</ul>
			</Popover>
		</span>
	</div>
</div>

<style>
	button {
		display: flex;
		align-items: flex-end;
		justify-content: center;
		block-size: 24px;
		inline-size: 48px;
		border: none;
		background-color: transparent;
	}

	ul {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
		background-color: #2a3039;
	}

	ul li {
		border-inline-end: 1px solid black;
		border-block-end: 1px solid black;
		padding: var(--gutterSmall);
	}

	:global(.formattingButton li .menuItem:hover span) {
		color: #3f8ce8 !important;
		cursor: pointer;
	}

	/* Reversing text-alignment icons in RTL  */
	:global(#pageContainer[dir='rtl'] .formattingButton .px-icon-paragraph-align-left::before) {
		content: '\E93c';
	}

	:global(#pageContainer[dir='rtl'] .formattingButton .px-icon-paragraph-align-right::before) {
		content: '\E93a';
	}
</style>
